import type { ChangeEvent, ReactNode } from 'react';
import {
  FilterShelf, PageHeader, Row, ScreenBody, Stack, type TabBarAction,
  type TabBarItem, } from '@leanin/ui';
import styles from './TabbedListPage.module.css';

export type TabbedListPageProps = {
  eyebrow?: ReactNode;
  back?: { label: string; href: string };
  /**
   * Page title. Required unless `hero` is supplied — when a hero banner is
   * present it owns the visual heading and `title` / `eyebrow` are unused.
   */
  title?: ReactNode;
  /**
   * Optional full-bleed hero element that replaces the `PageHeader`. When
   * supplied, `eyebrow`, `title`, `subtitle`, `preview`, and `headerAside`
   * are ignored — the hero owns that visual region. The hero is rendered with
   * negative margins to bleed to the edges of `ScreenBody`'s padding.
   */
  hero?: ReactNode;
  /**
   * Optional full-width module rendered above the column row (feed column +
   * aside) WITHOUT the hero bleed — it aligns with the content padding and
   * spans the aside too. Used by /feed for the leader action center. When
   * neither `hero`, `title`, nor `lead` is supplied the header region is
   * skipped entirely and the page starts at the sticky filter shelf.
   */
  lead?: ReactNode;
  subtitle?: string;
  /** PageHeader preview slot — for sub-header content that needs JSX (e.g. an
   *  inline link) that PageHeader's plain-string subtitle does not accept. */
  preview?: ReactNode;
  headerActions?: ReactNode;
  headerAside?: ReactNode;

  search?: {
    placeholder?: string;
    value: string;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    /** Control tier for the search pill — 'lg' for hero landing pages. */
    size?: 'md' | 'lg';
  };
  searchActions?: ReactNode;
  /**
   * Applied-filter chips. Rendered between the search row and the tabs so
   * the chips sit visually adjacent to the control that produced them —
   * a Filters button next to the search bar — and above the tabs, which
   * are an orthogonal scope dimension.
   */
  appliedFilters?: ReactNode;
  /** Tab strip. Omit entirely for single-scope list pages (e.g. the unified
   *  group directories) — FilterShelf skips the TabBar when absent. */
  tabs?: ReadonlyArray<TabBarItem>;
  /** Inline controls on the tab row's trailing edge (FilterShelf tabActions). */
  tabActions?: ReadonlyArray<TabBarAction>;
  /** Tab overflow behavior — `'scroll'` keeps tabs inline and scrolls them
   *  horizontally on small screens instead of collapsing into "More". */
  tabOverflow?: 'menu' | 'scroll';
  activeTab?: string;
  onTabChange?: (id: string) => void;

  beforeSticky?: ReactNode;
  children: ReactNode;
  aside?: ReactNode;
};

// Canonical layout for tabbed list pages. Owns the relationship between the
// page header, sticky search/tabs region, body, and optional sidebar so the
// pattern can't drift across features.
//
// Vertical rhythm:
//   PageHeader and the sticky filter region are one navigational unit — there
//   is NO gap between them. ScreenBody's `gap` is set to 0; spacing between
//   regions is owned by the immediate child (StickyBar's paddingBlock for
//   listing pages; beforeSticky's wrapper paddingTop for /feed).
//
// Scroll container:
//   This shell does NOT introduce its own scroll container — sticky positioning
//   pins to AppShell's <main>, not a nested wrapper. (Earlier code used <Screen>
//   here which created a nested scroll and broke sticky behavior.)
export function TabbedListPage({
  eyebrow,
  back,
  title,
  subtitle,
  preview,
  headerActions,
  headerAside,
  hero,
  lead,
  search,
  searchActions,
  appliedFilters,
  tabs,
  tabActions,
  tabOverflow,
  activeTab,
  onTabChange,
  beforeSticky,
  children,
  aside,
}: TabbedListPageProps) {
  return (
    <ScreenBody
      gap="none"
      style={{ background: 'var(--background)', minHeight: '100%' }}
    >
      {hero !== undefined ? (
        <div className={styles.heroBleed}>{hero}</div>
      ) : title !== undefined || eyebrow !== undefined || back !== undefined ? (
        <PageHeader
          eyebrow={eyebrow}
          back={back}
          title={title ?? null}
          subtitle={subtitle}
          preview={preview}
          aside={headerAside}
        />
      ) : null}
      {lead !== undefined ? <Stack pb="sm">{lead}</Stack> : null}
      <Row
        gap="lg"
        align="flex-start"
        wrap
        style={{
          // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- Row primitive accepts gap but not rowGap; wrapping case
          rowGap: 'var(--space-lg)',
        }}
      >
        <Stack
          gap="none"
          style={{
            flex: 1,
            minWidth: 320,
            background: 'var(--background)',
            overflow: 'clip',
          }}
        >
          {beforeSticky ? (
            <Stack gap="lg" pt="lg" pb="sm">
              {beforeSticky}
            </Stack>
          ) : null}
          <FilterShelf
            search={search !== undefined}
            searchPlaceholder={search?.placeholder ?? 'Search…'}
            searchValue={search?.value}
            onSearchChange={search?.onChange}
            searchSize={search?.size ?? 'md'}
            tabs={tabs}
            tabActions={tabActions}
            {...(tabOverflow !== undefined ? { tabOverflow } : {})}
            value={activeTab}
            onChange={onTabChange}
            actions={
              headerActions && searchActions ? (
                <Row gap="sm" align="center" wrap>{searchActions}{headerActions}</Row>
              ) : (headerActions ?? searchActions)
            }
            appliedFilters={appliedFilters}
          />
          <Stack gap="md">{children}</Stack>
        </Stack>
        {aside ? (
          <div
            style={{
              // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- raw <div> aside column with alignSelf override; no Box prop
              paddingTop: 'var(--space-lg)',
              alignSelf: 'stretch',
            }}
          >
            {aside}
          </div>
        ) : null}
      </Row>
    </ScreenBody>
  );
}
